import { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, RotateCcw, Check, Plus, Sparkles } from "lucide-react";
import { Reveal, BigHead } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BASE_PERSONALITIES = [
  "Bold", "Playful", "Minimal", "Luxury", "Professional", "Creative", "Edgy",
  "Friendly", "Elegant", "Vintage", "Fierce", "Chill", "Quirky", "Athletic",
];

const PALETTES = [
  { name: "Sunset Punch", colors: ["#FF7A00", "#FF4D6D", "#FFC233", "#1D1D1B"] },
  { name: "Ocean Pop", colors: ["#0077B6", "#00E0B0", "#0B1B3A", "#FFFFFF"] },
  { name: "Mono Studio", colors: ["#0A0A0A", "#FFFFFF", "#FFC233", "#BDBDBD"] },
  { name: "Candy Heat", colors: ["#FF4D6D", "#FF8FA3", "#FFB000", "#2B2B2B"] },
  { name: "Forest Floor", colors: ["#1B4332", "#95D5B2", "#F4F1DE", "#081C15"] },
  { name: "Cherry Cola", colors: ["#9B2226", "#EE6C4D", "#F4F1DE", "#220901"] },
  { name: "Peach Sorbet", colors: ["#FF9770", "#FFD670", "#8ECAE6", "#22223B"] },
  { name: "Midnight Neon", colors: ["#0D1B2A", "#00F5D4", "#F15BB5", "#FFFFFF"] },
  { name: "Desert Clay", colors: ["#C15C3C", "#E9C46A", "#2A9D8F", "#264653"] },
  { name: "Golden Noir", colors: ["#C9A227", "#111111", "#F5EFE0", "#3D2E05"] },
  { name: "Slime Time", colors: ["#A8E10C", "#FF4D6D", "#161616", "#FFFFFF"] },
  { name: "Spa Day", colors: ["#7FB69E", "#F2E8CF", "#2F5D50", "#12201B"] },
  { name: "Home Run", colors: ["#C1121F", "#003049", "#FDF0D5", "#0A0A0A"] },
];

const TYPE_STYLES = [
  { name: "Modern", family: "'Archivo', sans-serif", weight: 900, sample: "Aa" },
  { name: "Elegant", family: "'Playfair Display', serif", weight: 700, sample: "Aa" },
  { name: "Playful", family: "'Archivo', sans-serif", weight: 800, italic: true, sample: "Aa" },
  { name: "Editorial", family: "'Playfair Display', serif", weight: 600, italic: true, sample: "Aa" },
  { name: "Retro", family: "'Bungee', cursive", weight: 400, sample: "Aa" },
  { name: "Classic", family: "'DM Serif Display', serif", weight: 400, sample: "Aa" },
  { name: "Clean", family: "'Outfit', sans-serif", weight: 600, sample: "Aa" },
];

// First entry in each list = the single best match for that personality.
const MOOD_MAP = {
  Bold: { palettes: ["Candy Heat", "Midnight Neon", "Home Run"], types: ["Modern", "Retro", "Clean"] },
  Playful: { palettes: ["Peach Sorbet", "Slime Time", "Candy Heat"], types: ["Playful", "Retro", "Modern"] },
  Minimal: { palettes: ["Mono Studio", "Spa Day", "Desert Clay"], types: ["Clean", "Modern", "Classic"] },
  Luxury: { palettes: ["Golden Noir", "Mono Studio", "Cherry Cola"], types: ["Elegant", "Classic", "Editorial"] },
  Professional: { palettes: ["Ocean Pop", "Mono Studio", "Home Run"], types: ["Clean", "Modern", "Classic"] },
  Creative: { palettes: ["Sunset Punch", "Peach Sorbet", "Slime Time"], types: ["Playful", "Editorial", "Modern"] },
  Edgy: { palettes: ["Midnight Neon", "Cherry Cola", "Home Run"], types: ["Modern", "Retro", "Editorial"] },
  Friendly: { palettes: ["Peach Sorbet", "Spa Day", "Ocean Pop"], types: ["Playful", "Clean", "Modern"] },
  Elegant: { palettes: ["Golden Noir", "Cherry Cola", "Mono Studio"], types: ["Elegant", "Classic", "Editorial"] },
  Vintage: { palettes: ["Desert Clay", "Cherry Cola", "Sunset Punch"], types: ["Classic", "Editorial", "Retro"] },
  Fierce: { palettes: ["Candy Heat", "Home Run", "Midnight Neon"], types: ["Modern", "Retro", "Clean"] },
  Chill: { palettes: ["Ocean Pop", "Spa Day", "Forest Floor"], types: ["Clean", "Editorial", "Classic"] },
  Quirky: { palettes: ["Slime Time", "Peach Sorbet", "Sunset Punch"], types: ["Playful", "Retro", "Editorial"] },
  Athletic: { palettes: ["Home Run", "Midnight Neon", "Ocean Pop"], types: ["Modern", "Clean", "Retro"] },
};

// Custom typed adjectives get matched to the closest known vibe.
const KEYWORD_MOODS = [
  [["sport", "gym", "fit", "athlet", "strong", "muscle"], "Athletic"],
  [["lux", "fancy", "premium", "rich", "posh", "high-end", "high end"], "Luxury"],
  [["fun", "play", "silly", "bubble", "party"], "Playful"],
  [["calm", "zen", "relax", "soft", "chill", "cozy"], "Chill"],
  [["corp", "prof", "business", "formal", "serious"], "Professional"],
  [["art", "creat", "design", "imag"], "Creative"],
  [["retro", "vint", "old", "nostal", "throwback"], "Vintage"],
  [["cute", "friend", "warm", "kind", "happy"], "Friendly"],
  [["weird", "quirk", "funky", "odd", "wacky"], "Quirky"],
  [["min", "clean", "simple", "sleek", "neat"], "Minimal"],
  [["eleg", "grace", "refin", "class"], "Elegant"],
  [["edge", "edgy", "punk", "dark", "rebel", "grit", "grunge"], "Edgy"],
  [["fierce", "fire", "savage", "power", "beast"], "Fierce"],
  [["bold", "loud", "brave", "daring"], "Bold"],
];

const moodFor = (p) => {
  if (!p) return null;
  if (MOOD_MAP[p]) return MOOD_MAP[p];
  const low = p.toLowerCase();
  for (const [keys, mood] of KEYWORD_MOODS) {
    if (keys.some((k) => low.includes(k))) return MOOD_MAP[mood];
  }
  return null;
};

// Merge the curation of every selected adjective (deduped, best matches first).
const mergedMoodFor = (list) => {
  const maps = list.map(moodFor).filter(Boolean);
  if (!maps.length) return null;
  return {
    palettes: [...new Set(maps.flatMap((m) => m.palettes))],
    types: [...new Set(maps.flatMap((m) => m.types))],
  };
};

const LOGO_STYLES = [
  { name: "Wordmark", blurb: "Your name IS the logo — styled type, nothing else.", eg: "Think Google or Coca-Cola." },
  { name: "Monogram", blurb: "Initials distilled into a mark. Perfect for long names.", eg: "Think HBO or Louis Vuitton." },
  { name: "Icon", blurb: "A simple symbol next to your name, readable at any size.", eg: "Think Apple or Target." },
  { name: "Abstract", blurb: "A custom shape that becomes unmistakably yours.", eg: "Think the STAX circles." },
  { name: "Badge", blurb: "Type sealed inside an emblem — crafty, vintage, official.", eg: "Think Starbucks or Harley-Davidson." },
];

const STEPS = ["Personality", "Colors", "Type", "Logo", "Name"];

const lum = (hex) => {
  const n = parseInt(hex.slice(1), 16);
  return 0.2126 * ((n >> 16) & 255) + 0.7152 * ((n >> 8) & 255) + 0.0722 * (n & 255);
};
const inkOn = (hex) => (lum(hex) < 130 ? "#FFFFFF" : "#0A0A0A");
// Some palettes end in white — never render logo text in a near-invisible color.
const safeText = (hex) => (lum(hex) > 180 ? "#0A0A0A" : hex);

const LogoPreview = ({ style, name, colors, type }) => {
  const n = name || "YOUR BRAND";
  const font = { fontFamily: type.family, fontWeight: type.weight, fontStyle: type.italic ? "italic" : "normal" };
  const textColor = safeText(colors[3]);
  if (style === "Monogram")
    return (
      <span className="flex h-16 w-16 items-center justify-center rounded-full border-4" style={{ borderColor: textColor, background: colors[0] }}>
        <span style={{ ...font, color: inkOn(colors[0]), fontSize: 24 }}>{n.slice(0, 1).toUpperCase()}</span>
      </span>
    );
  if (style === "Icon")
    return (
      <span className="flex items-center gap-3">
        <span className="flex -space-x-3">
          {colors.slice(0, 3).map((c, i) => (
            <span key={i} className="ink-dot h-9 w-9" style={{ background: c }} />
          ))}
        </span>
        <span style={{ ...font, color: textColor, fontSize: 22 }} className="uppercase">{n}</span>
      </span>
    );
  if (style === "Abstract")
    return (
      <span className="flex items-center gap-3">
        <span className="relative block h-10 w-14">
          <span className="ink-dot absolute left-0 top-0 h-10 w-10" style={{ background: colors[0] }} />
          <span className="ink-dot absolute left-5 top-0 h-10 w-10" style={{ background: colors[1] }} />
        </span>
        <span style={{ ...font, color: textColor, fontSize: 22 }} className="uppercase">{n}</span>
      </span>
    );
  if (style === "Badge")
    return (
      <span className="border-4 px-5 py-2.5" style={{ borderColor: textColor, background: colors[1] }}>
        <span style={{ ...font, color: inkOn(colors[1]), fontSize: 20 }} className="uppercase tracking-wide">{n}</span>
      </span>
    );
  return <span data-testid="logo-preview-wordmark" style={{ ...font, color: textColor, fontSize: 30 }} className="uppercase leading-none">{n}</span>;
};

export const BrandKit = () => {
  const [step, setStep] = useState(0);
  const [personalities, setPersonalities] = useState([]);
  const [palette, setPalette] = useState(null);
  const [type, setType] = useState(null);
  const [logo, setLogo] = useState(null);
  const [brandName, setBrandName] = useState("");
  const [lead, setLead] = useState({ name: "", email: "" });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [customs, setCustoms] = useState([]);
  const [customText, setCustomText] = useState("");
  const [customColors, setCustomColors] = useState(["#FFC233", "#EC008C", "#127DDD", "#252827"]);
  const [customPaletteOn, setCustomPaletteOn] = useState(false);

  const allPersonalities = [...BASE_PERSONALITIES, ...customs];
  const mood = mergedMoodFor(personalities);
  const label = personalities.join(" • ");

  // Curated picks first (in best-match order), everything else after.
  const recPalettes = mood ? mood.palettes.map((n) => PALETTES.find((p) => p.name === n)).filter(Boolean) : [];
  const morePalettes = mood ? PALETTES.filter((p) => !mood.palettes.includes(p.name)) : PALETTES;
  const recTypes = mood ? mood.types.map((n) => TYPE_STYLES.find((t) => t.name === n)).filter(Boolean) : [];
  const moreTypes = mood ? TYPE_STYLES.filter((t) => !mood.types.includes(t.name)) : TYPE_STYLES;

  const picks = [personalities.length ? personalities : null, palette, type, logo, brandName.trim()];
  const canNext = picks[step] !== null && picks[step] !== undefined && picks[step] !== "";
  const done = step === 5;

  const board = useMemo(() => ({ palette, type }), [palette, type]);

  const pickPersonality = (p) => {
    const next = personalities.includes(p)
      ? personalities.filter((x) => x !== p)
      : [...personalities, p];
    setPersonalities(next);
    const merged = mergedMoodFor(next);
    if (merged) {
      // Auto-select the best-matching palette & type so the board instantly fits the vibe.
      setPalette(PALETTES.find((x) => x.name === merged.palettes[0]));
      setType(TYPE_STYLES.find((t) => t.name === merged.types[0]));
      setCustomPaletteOn(false);
    } else {
      setPalette(null);
      setType(null);
    }
  };

  const addCustom = () => {
    const v = customText.trim();
    if (!v) return;
    const cap = v.charAt(0).toUpperCase() + v.slice(1);
    if (!allPersonalities.includes(cap)) setCustoms((c) => [...c, cap]);
    pickPersonality(cap);
    setCustomText("");
  };

  const pickCustomColor = (i, value) => {
    const next = customColors.map((c, j) => (j === i ? value : c));
    setCustomColors(next);
    setCustomPaletteOn(true);
    setPalette({ name: "Custom Palette", colors: next });
  };

  const reset = () => {
    setStep(0); setPersonalities([]); setPalette(null); setType(null); setLogo(null);
    setBrandName(""); setSent(false); setLead({ name: "", email: "" });
    setCustoms([]); setCustomText(""); setCustomPaletteOn(false);
    setCustomColors(["#FFC233", "#EC008C", "#127DDD", "#252827"]);
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead.name.trim() || !lead.email.trim()) {
      toast.error("Add your name and email so STAX can reach you.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/brand-kit`, {
        name: lead.name, email: lead.email, brand_name: brandName,
        personality: label, palette: palette.name, typography: type.name, logo_style: logo,
      });
      setSent(true);
      toast.success("Your brand kit is on its way to STAX!");
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setSending(false);
    }
  };

  const chip = (active) =>
    `inline-flex items-center gap-1.5 border-2 border-ink px-4 py-2.5 font-mono2 text-xs font-bold uppercase tracking-[0.15em] transition-all ${
      active ? "text-ink -translate-y-0.5 shadow-hard-sm" : "bg-white text-ink hover:bg-staxyellow"
    }`;

  const demoColors = palette?.colors ?? PALETTES[0].colors;
  const demoType = type ?? TYPE_STYLES[0];

  const divider = (text) => (
    <div className="col-span-full mt-2 flex items-center gap-4">
      <span className="h-0.5 flex-1 bg-ink/15" />
      <span className="font-mono2 text-[10px] font-bold uppercase tracking-[0.25em] text-ink/45">{text}</span>
      <span className="h-0.5 flex-1 bg-ink/15" />
    </div>
  );

  const paletteCard = (pl, badge) => (
    <button key={pl.name} data-testid={`palette-${pl.name.toLowerCase().replace(/\s+/g, "-")}`}
      onClick={() => { setPalette(pl); setCustomPaletteOn(false); }}
      className={`border-2 border-ink bg-white p-3 text-left transition-all ${!customPaletteOn && palette?.name === pl.name ? "shadow-hard-sm -translate-y-0.5 ring-4 ring-staxmagenta" : "hover:-translate-y-0.5"}`}>
      <div className="flex h-14">
        {pl.colors.map((c, i) => (
          <span key={i} className="flex-1 first:rounded-l-none border border-ink/20" style={{ background: c }} />
        ))}
      </div>
      <span className="mt-2 flex items-center justify-between gap-2">
        <span className="font-mono2 text-xs font-bold uppercase tracking-widest">{pl.name}</span>
        {badge && (
          <span data-testid={badge === "best" ? "palette-best-match" : undefined}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 font-mono2 text-[8px] font-bold uppercase tracking-[0.15em] ${badge === "best" ? "bg-staxmagenta text-white" : "bg-ink text-staxyellow"}`}>
            {badge === "best" && <Sparkles size={8} />}
            {badge === "best" ? "Best match" : "Great fit"}
          </span>
        )}
      </span>
    </button>
  );

  const typeCard = (t, badge) => (
    <button key={t.name} data-testid={`type-${t.name.toLowerCase()}`} onClick={() => setType(t)}
      className={`border-2 border-ink bg-white p-4 text-center transition-all ${type?.name === t.name ? "shadow-hard-sm -translate-y-0.5 ring-4 ring-staxcyan" : "hover:-translate-y-0.5"}`}>
      <span className="block text-4xl text-ink" style={{ fontFamily: t.family, fontWeight: t.weight, fontStyle: t.italic ? "italic" : "normal" }}>
        {t.sample}
      </span>
      <span className="mt-2 block font-mono2 text-[10px] font-bold uppercase tracking-widest text-ink/60">{t.name}</span>
      {badge && (
        <span data-testid={badge === "best" ? "type-best-match" : undefined}
          className={`mt-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 font-mono2 text-[8px] font-bold uppercase tracking-[0.15em] ${badge === "best" ? "bg-staxmagenta text-white" : "bg-ink text-staxyellow"}`}>
          {badge === "best" && <Sparkles size={8} />}
          {badge === "best" ? "Best match" : "Great fit"}
        </span>
      )}
    </button>
  );

  return (
    <section id="brand-kit" data-testid="brand-kit-section" className="relative overflow-hidden bg-staxyellow py-24 sm:py-32 border-y-4 border-ink">
      <div className="absolute -left-24 top-10 h-72 w-72 rounded-full bg-staxmagenta pointer-events-none" />
      <div className="ink-dot absolute -right-20 bottom-0 h-80 w-80 bg-staxcyan" />
      <div className="relative mx-auto max-w-6xl px-4 sm:px-8">
        <div className="text-center">
          <Reveal delay={0.08}>
            <BigHead className="mt-6">Build Your<br />Brand Kit</BigHead>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="mx-auto mt-5 max-w-md font-medium text-ink/70 text-base sm:text-lg">
              Your brand. Your colors. Your vibe. Let's make it official.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          <div className="relative mt-12 border-4 border-ink bg-paper shadow-hard">
            <div className="flex items-center justify-between border-b-4 border-ink bg-white px-4 sm:px-6 py-3 overflow-x-auto">
              <div className="flex gap-2">
                {STEPS.map((s, i) => (
                  <span key={s} data-testid={`brandkit-step-${i}`}
                    className={`flex items-center gap-1.5 font-mono2 text-[10px] font-bold uppercase tracking-[0.15em] whitespace-nowrap ${i < step || done ? "text-ink" : i === step ? "text-staxmagenta" : "text-ink/35"}`}>
                    <span className={`flex h-5 w-5 items-center justify-center rounded-full border-2 border-ink text-[9px] ${i < step || done ? "bg-staxcyan" : i === step ? "bg-staxmagenta text-white" : "bg-white"}`}>
                      {i < step || done ? <Check size={10} /> : i + 1}
                    </span>
                    <span className="hidden sm:inline">{s}</span>
                  </span>
                ))}
              </div>
              <button data-testid="brandkit-reset-button" onClick={reset} className="ml-3 flex items-center gap-1 font-mono2 text-[10px] font-bold uppercase tracking-widest text-ink/50 hover:text-staxmagenta">
                <RotateCcw size={12} /> Reset
              </button>
            </div>

            <div className="min-h-[380px] p-6 sm:p-10">
              <AnimatePresence mode="wait">
                <motion.div key={step} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.3 }}>
                  {step === 0 && (
                    <div>
                      <h3 className="font-display uppercase text-2xl sm:text-3xl">1 — Pick your brand personality</h3>
                      <p className="mt-2 font-mono2 text-xs uppercase tracking-widest text-ink/50">
                        Pick as many as fit — tap again to unselect.
                      </p>
                      <div data-testid="brandkit-personalities" className="mt-6 flex flex-wrap gap-3">
                        {allPersonalities.map((p) => {
                          const on = personalities.includes(p);
                          return (
                            <button key={p} data-testid={`personality-${p.toLowerCase()}`} onClick={() => pickPersonality(p)}
                              aria-pressed={on}
                              className={chip(on)} style={on ? { background: "#EC008C", color: "#fff" } : {}}>
                              {on && <Check size={12} />}
                              {p}
                            </button>
                          );
                        })}
                      </div>
                      <div className="mt-6 flex max-w-md gap-3">
                        <input
                          data-testid="brandkit-custom-input"
                          value={customText}
                          onChange={(e) => setCustomText(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && addCustom()}
                          placeholder="Or type your own adjective..."
                          maxLength={18}
                          className="flex-1 border-2 border-ink bg-white px-4 py-2.5 font-mono2 text-xs font-bold uppercase tracking-[0.15em] focus:outline-none focus:ring-2 focus:ring-staxmagenta placeholder:text-ink/35"
                        />
                        <button
                          data-testid="brandkit-custom-add"
                          onClick={addCustom}
                          className="inline-flex items-center gap-1.5 border-2 border-ink bg-ink px-4 py-2.5 font-mono2 text-xs font-bold uppercase tracking-[0.15em] text-white transition-colors hover:bg-staxmagenta"
                        >
                          <Plus size={14} /> Add
                        </button>
                      </div>
                      {personalities.length > 0 && (
                        <p data-testid="brandkit-mood-note" className="mt-5 font-mono2 text-xs uppercase tracking-widest text-ink/50">
                          {mood
                            ? `"${label}" energy — we've pre-matched colors & type on the next steps. Swap anything you like.`
                            : `"${label}" is a wildcard — everything stays on the table.`}
                        </p>
                      )}
                    </div>
                  )}
                  {step === 1 && (
                    <div>
                      <h3 className="font-display uppercase text-2xl sm:text-3xl">
                        {mood ? <>2 — Colors that feel <span className="text-staxmagenta">"{label}"</span></> : "2 — Choose a color palette"}
                      </h3>
                      <p className="mt-2 font-mono2 text-xs uppercase tracking-widest text-ink/50">
                        {mood ? "Pre-matched to your vibe — the best one is already selected. Swap it if you want." : "Every palette, no filter."}
                      </p>
                      <div data-testid="brandkit-palettes" className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {recPalettes.map((pl, i) => paletteCard(pl, i === 0 ? "best" : "fit"))}
                        {mood && divider("More palettes")}
                        {morePalettes.map((pl) => paletteCard(pl, null))}
                        {divider("Or build your own")}
                        <div data-testid="palette-custom-card"
                          className={`border-2 border-ink bg-white p-3 text-left transition-all sm:col-span-2 lg:col-span-3 ${customPaletteOn ? "shadow-hard-sm ring-4 ring-staxmagenta" : ""}`}>
                          <p className="font-mono2 text-xs font-bold uppercase tracking-widest">Custom Palette — pick your own 4 colors</p>
                          <div className="mt-3 flex h-14">
                            {customColors.map((c, i) => (
                              <label key={i} className="relative flex-1 cursor-pointer border border-ink/20" style={{ background: c }}
                                data-testid={`custom-color-${i}`}>
                                <input type="color" value={c} onChange={(e) => pickCustomColor(i, e.target.value)}
                                  className="absolute inset-0 h-full w-full cursor-pointer opacity-0" aria-label={`Custom color ${i + 1}`} />
                              </label>
                            ))}
                          </div>
                          <p className="mt-2 font-mono2 text-[10px] uppercase tracking-widest text-ink/45">
                            Tap a swatch to change it — this becomes your palette.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  {step === 2 && (
                    <div>
                      <h3 className="font-display uppercase text-2xl sm:text-3xl">
                        {mood ? <>3 — Type that talks <span className="text-staxmagenta">"{label}"</span></> : "3 — Pick a typography style"}
                      </h3>
                      <p className="mt-2 font-mono2 text-xs uppercase tracking-widest text-ink/50">
                        {mood ? "Pre-matched to your vibe — the best one is already selected. Swap it if you want." : "Every type style, no filter."}
                      </p>
                      <div data-testid="brandkit-type-styles" className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                        {recTypes.map((t, i) => typeCard(t, i === 0 ? "best" : "fit"))}
                        {mood && divider("More type styles")}
                        {moreTypes.map((t) => typeCard(t, null))}
                      </div>
                    </div>
                  )}
                  {step === 3 && (
                    <div>
                      <h3 className="font-display uppercase text-2xl sm:text-3xl">4 — Choose a logo style</h3>
                      <p className="mt-2 font-mono2 text-xs uppercase tracking-widest text-ink/50">
                        Each one previewed live with your picks.
                      </p>
                      <div data-testid="brandkit-logo-styles" className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {LOGO_STYLES.map((l) => (
                          <button key={l.name} data-testid={`logo-${l.name.toLowerCase()}`} onClick={() => setLogo(l.name)}
                            className={`border-2 border-ink bg-white p-5 text-left transition-all ${logo === l.name ? "shadow-hard-sm -translate-y-0.5 ring-4 ring-staxcyan" : "hover:-translate-y-0.5"}`}>
                            <div className="flex h-16 items-center">
                              <LogoPreview style={l.name} name={brandName || "Your Brand"} colors={demoColors} type={demoType} />
                            </div>
                            <span className="mt-3 block font-display uppercase text-lg text-ink">{l.name}</span>
                            <span className="mt-1 block text-xs font-medium text-ink/60 leading-relaxed">{l.blurb}</span>
                            <span className="mt-2 block font-mono2 text-[10px] font-bold uppercase tracking-widest text-staxmagenta">{l.eg}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {step === 4 && (
                    <div>
                      <h3 className="font-display uppercase text-2xl sm:text-3xl">5 — What's your brand called?</h3>
                      <input
                        data-testid="brandkit-name-input"
                        value={brandName}
                        onChange={(e) => setBrandName(e.target.value.slice(0, 24))}
                        placeholder="e.g. Rocket Taco"
                        className="mt-6 w-full max-w-lg border-4 border-ink bg-white px-5 py-4 font-display uppercase text-2xl focus:outline-none focus:ring-4 focus:ring-staxmagenta placeholder:text-ink/25"
                      />
                      <p className="mt-3 font-mono2 text-xs uppercase tracking-widest text-ink/50">This becomes the star of your brand board.</p>
                    </div>
                  )}

                  {done && board.palette && board.type && (
                    <div data-testid="brandkit-board">
                      <div className="flex flex-wrap items-center justify-between gap-4">
                        <h3 className="font-display uppercase text-3xl sm:text-4xl">Your Mini Brand Board</h3>
                        <span className="border-2 border-ink bg-staxmagenta px-3 py-1.5 font-mono2 text-xs font-bold uppercase tracking-widest text-white">
                          {label} • {palette.name}
                        </span>
                      </div>

                      <div className="mt-8 grid grid-cols-1 lg:grid-cols-5 gap-6">
                        <div className="lg:col-span-3 border-4 border-ink bg-white p-8 shadow-hard-sm">
                          <p className="font-mono2 text-[10px] font-bold uppercase tracking-[0.3em] text-ink/50">Logo — {logo}</p>
                          <div data-testid="brandkit-board-logo" className="mt-5 min-h-[90px] flex items-center">
                            <LogoPreview style={logo} name={brandName} colors={palette.colors} type={type} />
                          </div>
                          <div className="mt-8 border-t-2 border-dashed border-ink/20 pt-6">
                            <p className="font-mono2 text-[10px] font-bold uppercase tracking-[0.3em] text-ink/50">Sample headline</p>
                            <p className="mt-2 uppercase leading-[0.95] text-ink" style={{ fontFamily: type.family, fontWeight: type.weight, fontStyle: type.italic ? "italic" : "normal", fontSize: "2.2rem" }}>
                              {brandName || "Your Brand"} makes people look twice.
                            </p>
                            <button data-testid="brandkit-sample-button" className="mt-5 border-2 border-ink px-5 py-2.5 font-display uppercase text-sm"
                              style={{ background: palette.colors[1], color: inkOn(palette.colors[1]) }}>
                              Get Started
                            </button>
                          </div>
                        </div>

                        <div className="lg:col-span-2 space-y-6">
                          <div className="border-4 border-ink bg-white p-6 shadow-hard-sm">
                            <p className="font-mono2 text-[10px] font-bold uppercase tracking-[0.3em] text-ink/50">Palette</p>
                            <div className="mt-3 flex">
                              {palette.colors.map((c, i) => (
                                <div key={i} className="flex-1">
                                  <div className="h-16 border border-ink/20" style={{ background: c }} />
                                  <p className="mt-1 font-mono2 text-[9px] uppercase text-ink/50">{c}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="border-4 border-ink shadow-hard-sm overflow-hidden">
                            <div className="relative aspect-square" style={{ background: palette.colors[0] }}>
                              <span className="ink-dot absolute -right-6 -top-6 h-24 w-24" style={{ background: palette.colors[1] }} />
                              <span className="ink-dot absolute right-10 top-12 h-16 w-16" style={{ background: palette.colors[2] }} />
                              <div className="absolute left-5 bottom-5 right-5">
                                <p className="uppercase leading-[0.9]" style={{ fontFamily: type.family, fontWeight: type.weight, color: inkOn(palette.colors[0]), fontSize: "1.8rem" }}>
                                  {brandName || "Your Brand"}
                                </p>
                                <p className="mt-1 font-mono2 text-[10px] font-bold uppercase tracking-[0.25em]" style={{ color: inkOn(palette.colors[0]) }}>
                                  {label || "Custom"} energy. Daily.
                                </p>
                              </div>
                            </div>
                            <p className="border-t-2 border-ink bg-white px-3 py-2 font-mono2 text-[10px] font-bold uppercase tracking-widest text-ink/50">
                              Sample social post
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="mt-10 border-4 border-ink bg-ink p-8 text-center shadow-hard-sm">
                        <p className="font-display uppercase text-3xl sm:text-4xl text-white leading-tight">
                          Love what you built?<br /><span className="text-staxyellow">Let's make it real.</span>
                        </p>
                        {sent ? (
                          <p data-testid="brandkit-sent-message" className="mt-6 inline-flex items-center gap-2 border-2 border-staxyellow px-5 py-3 font-mono2 text-sm font-bold uppercase tracking-widest text-staxyellow">
                            <Check size={16} /> Kit sent — STAX will reach out soon
                          </p>
                        ) : (
                          <form onSubmit={submitLead} className="mx-auto mt-7 flex max-w-lg flex-col sm:flex-row gap-3">
                            <input data-testid="brandkit-lead-name" value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })}
                              placeholder="Your name" className="flex-1 border-2 border-white bg-transparent px-4 py-3 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-staxyellow" />
                            <input data-testid="brandkit-lead-email" type="email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })}
                              placeholder="Email" className="flex-1 border-2 border-white bg-transparent px-4 py-3 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-staxyellow" />
                            <button data-testid="brandkit-submit-button" type="submit" disabled={sending}
                              className="group inline-flex items-center justify-center gap-2 border-2 border-staxyellow bg-staxyellow px-5 py-3 font-display uppercase text-sm text-ink transition-transform hover:-translate-y-0.5 disabled:opacity-60">
                              {sending ? "Sending..." : "Work With STAX"} <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                            </button>
                          </form>
                        )}
                      </div>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>

            {!done && (
              <div className="flex items-center justify-between border-t-4 border-ink bg-white px-4 sm:px-6 py-4">
                <button data-testid="brandkit-back-button" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}
                  className="inline-flex items-center gap-2 border-2 border-ink bg-white px-5 py-2.5 font-display uppercase text-sm disabled:opacity-30 hover:bg-staxyellow transition-colors">
                  <ArrowLeft size={16} /> Back
                </button>
                <button data-testid="brandkit-next-button" onClick={() => canNext && setStep((s) => s + 1)} disabled={!canNext}
                  className={`inline-flex items-center gap-2 border-2 border-ink px-6 py-2.5 font-display uppercase text-sm transition-all ${canNext ? "bg-staxmagenta text-white shadow-hard-sm hover:-translate-y-0.5" : "bg-white opacity-40"}`}>
                  {step === 4 ? "Generate My Brand Board" : "Next"} <ArrowRight size={16} />
                </button>
              </div>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
